import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Badge, 
  Form,
  Alert,
  ListGroup,
  Image,
  Spinner
} from 'react-bootstrap';
import { PersonCircle, Upload } from 'react-bootstrap-icons';
import { useAuth } from './AuthContext';
import userApi from '../../api/userApi';
import actorApi from '../../api/actorApi';
import { ACCOUNT_LANGUAGES, getAccountLanguage } from '../../utils/accountLanguage';

const UserProfile = () => {
  const { user, refreshUser, updateAccountLanguage, updateHeroPreferences } = useAuth();
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [savingHeroPreferences, setSavingHeroPreferences] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleLanguageChange = async (event) => {
    setSavingLanguage(true);
    setFormError('');
    setFormSuccess('');
    try {
      await updateAccountLanguage(event.target.value);
      setFormSuccess('Kontosprache gespeichert. Chats werden automatisch in dieser Sprache angezeigt.');
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleHeroVisibilityChange = async (event) => {
    const enabled = event.target.checked;
    setSavingHeroPreferences(true);
    setFormError('');
    setFormSuccess('');
    try {
      await updateHeroPreferences({
        enabled,
        position: user.hero_position || { x: 1, y: 1 },
      });
      setFormSuccess(enabled ? 'eScooter Hero eingeschaltet.' : 'eScooter Hero für dieses Benutzerkonto ausgeschaltet.');
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSavingHeroPreferences(false);
    }
  };

  // Helper debug function to log profile picture URL
  useEffect(() => {
    console.log('User profile data:', user);
    if (user && user.profile_picture_url) {
      console.log('Profile picture URL:', user.profile_picture_url);
    }
  }, [user]);

  // Function to get a properly formatted profile picture URL with necessary parameters
  const getFormattedProfilePictureUrl = () => {
    if (!user || !user.profile_picture_url) return null;
    
    // Use actorApi to get correct URL based on environment
    if (user.actor_id) {
      const url = actorApi.getProfilePictureUrl(user.actor_id);
      console.log('UserProfile - Using profile picture URL:', url);
      return url;
    }
    
    // Fallback: add a cache buster parameter to the URL
    const separator = user.profile_picture_url.includes('?') ? '&' : '?';
    const url = `${user.profile_picture_url}${separator}cache=${Date.now()}`;
    console.log('UserProfile - Using fallback profile picture URL:', url);
    return url;
  };

  if (!user) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          You must be logged in to view your profile.
        </Alert>
      </Container>
    );
  }

  // Format timestamp
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  // Handle password form change
  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({
      ...passwordForm,
      [name]: value
    });
  };

  // Toggle password form
  const togglePasswordForm = () => {
    setShowChangePassword(!showChangePassword);
    setFormError('');
    setFormSuccess('');
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  // Handle profile picture upload
  const handleProfilePictureUpload = async (file) => {
    setIsUploading(true);
    setFormError(null);
    
    // Create form data for the image file
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('Starting profile picture upload...');
      
      // Use the existing userApi.uploadProfilePicture function 
      // which is already correctly configured with the right endpoint
      const response = await userApi.uploadProfilePicture(formData);
      
      console.log('Profile picture upload successful:', response);
      
      // Refresh user data to get the updated profile picture
      await refreshUser();
      
      setFormSuccess('Profile picture updated successfully');
      setIsUploading(false);
      return response;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setFormError(error.message || 'An error occurred while uploading the profile picture');
      setIsUploading(false);
      return null;
    }
  };

  // Handle file selection for profile picture
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.match('image.*')) {
      setFormError('Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setFormError('Image size should be less than 5MB');
      return;
    }

    await handleProfilePictureUpload(file);
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    
    // Validate form
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setFormError('All fields are required');
      return;
    }
    
    // SECURITY FIX: Enhanced password validation (OWASP recommendations)
    const password = passwordForm.newPassword;
    
    if (password.length < 12) {
      setFormError('Password must be at least 12 characters long');
      return;
    }
    
    if (!/[a-z]/.test(password)) {
      setFormError('Password must include at least one lowercase letter');
      return;
    }
    
    if (!/[A-Z]/.test(password)) {
      setFormError('Password must include at least one uppercase letter');
      return;
    }
    
    if (!/\d/.test(password)) {
      setFormError('Password must include at least one number');
      return;
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setFormError('Password must include at least one special character (!@#$%^&*...)');
      return;
    }
    
    // Check for common passwords
    const commonPasswords = ['password123', '123456789', 'qwerty123', 'Password123'];
    if (commonPasswords.some(common => password.toLowerCase().includes(common.toLowerCase()))) {
      setFormError('Password is too common. Please choose a more unique password');
      return;
    }
    
    // Check for repeated characters
    if (/(.)\1{2,}/.test(password)) {
      setFormError('Password should not contain repeated characters (e.g., "aaa")');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await userApi.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setFormSuccess('Password changed successfully');
      setShowChangePassword(false);
      
      // Refresh user info
      await refreshUser();
    } catch (error) {
      setFormError(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <h2 className="mb-4">Your Profile</h2>
      
      <Row>
        <Col md={4}>
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Profile Information</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-3">
                <div className="profile-picture-container">
                  {isUploading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ width: '100px', height: '100px' }}>
                      <Spinner animation="border" />
                    </div>
                  ) : user.profile_picture_url ? (
                    <div className="position-relative mx-auto" style={{ width: '100px', height: '100px' }}>
                      <Image 
                        src={getFormattedProfilePictureUrl()} 
                        roundedCircle 
                        width="100" 
                        height="100" 
                        style={{ objectFit: 'cover', border: '1px solid #dee2e6' }}
                        onError={(e) => {
                          console.log("Error loading profile image, using fallback");
                          e.target.onError = null;
                          e.target.style.display = 'none';
                          const placeholder = document.createElement('div');
                          placeholder.className = "rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center";
                          placeholder.style.width = '100px';
                          placeholder.style.height = '100px';
                          placeholder.style.fontSize = '2.5rem';
                          // SECURITY FIX: Use textContent instead of innerHTML to prevent XSS
                          placeholder.textContent = user.email.substring(0, 1).toUpperCase();
                          e.target.parentNode.appendChild(placeholder);
                        }}
                      />
                      {/* Delete button (X) removed as requested */}
                    </div>
                  ) : (
                    <div 
                      className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center mx-auto"
                      style={{ width: '100px', height: '100px', fontSize: '2.5rem' }}
                    >
                      {user.email.substring(0, 1).toUpperCase()}
                    </div>
                  )}
                  
                </div>
                
                <h4 className="mt-2">{user.email}</h4>
                
                {user.is_admin && (
                  <Badge bg="dark" className="px-3 py-2 mt-2">Administrator</Badge>
                )}
              </div>
              
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Form.Label htmlFor="account-chat-language"><strong>Chat-Sprache</strong></Form.Label>
                  <Form.Select id="account-chat-language" value={getAccountLanguage(user)}
                    onChange={handleLanguageChange} disabled={savingLanguage}>
                    {ACCOUNT_LANGUAGES.map((option) => (
                      <option key={option.key} value={option.key}>{option.nativeName}</option>
                    ))}
                  </Form.Select>
                  <Form.Text muted>
                    Alle Chatnachrichten werden live in Ihrer Kontosprache angezeigt.
                    Antworten werden in der Kundensprache versendet.
                  </Form.Text>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Form.Check type="switch" id="account-hero-enabled"
                    label="eScooter Hero anzeigen"
                    checked={user.hero_enabled !== false}
                    onChange={handleHeroVisibilityChange}
                    disabled={savingHeroPreferences} />
                  <Form.Text muted>
                    Diese Einstellung gilt auf allen Geräten. Zum Verschieben den Griff im Chatkopf ziehen oder mit den Pfeiltasten bewegen. Sie können Hero hier jederzeit wieder einschalten.
                  </Form.Text>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <strong>Status:</strong>
                  <Badge bg={
                    user.status === 'active' ? 'success' : 
                    user.status === 'pending_password_change' ? 'warning' : 
                    user.status === 'banned' ? 'danger' : 'secondary'
                  }>
                    {user.status ? user.status.replace('_', ' ') : ''}
                  </Badge>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <strong>Last Login:</strong>
                  <span>{formatDateTime(user.last_login)}</span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between">
                  <strong>Account Created:</strong>
                  <span>{formatDateTime(user.created_at)}</span>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
            <Card.Footer>
              <Button 
                variant="primary" 
                className="w-100"
                onClick={togglePasswordForm}
              >
                {showChangePassword ? 'Cancel' : 'Change Password'}
              </Button>
            </Card.Footer>
          </Card>
        </Col>
        
        <Col md={8}>
          {formSuccess && (
            <Alert variant="success" className="mb-4">
              {formSuccess}
            </Alert>
          )}
          
          {showChangePassword ? (
            <Card>
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">Change Password</h5>
              </Card.Header>
              <Card.Body>
                {formError && (
                  <Alert variant="danger">{formError}</Alert>
                )}
                
                <Form onSubmit={handlePasswordChange}>
                  <Form.Group className="mb-3" controlId="currentPassword">
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordFormChange}
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3" controlId="newPassword">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordFormChange}
                      required
                      minLength={12}
                    />
                    <Form.Text className="text-muted">
                      Password must be at least 12 characters with uppercase, lowercase, number, and special character (!@#$%^&*...)
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-4" controlId="confirmPassword">
                    <Form.Label>Confirm New Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordFormChange}
                      required
                    />
                  </Form.Group>
                  
                  <div className="d-flex justify-content-end gap-2">
                    <Button 
                      variant="secondary" 
                      onClick={togglePasswordForm}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Change Password'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">Your Roles and Permissions</h5>
              </Card.Header>
              <Card.Body>
                <h6 className="mb-3">Assigned Roles:</h6>
                
                {user.roles.length === 0 ? (
                  <Alert variant="info">
                    You don&apos;t have any specific roles assigned. Please contact an administrator if you need access to specific features.
                  </Alert>
                ) : (
                  <div className="mb-4">
                    {user.roles.map(role => (
                      <Badge 
                        key={role} 
                        bg="info" 
                        className="me-2 mb-2 p-2"
                      >
                        {role}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <h6 className="mb-3">Access Level Information:</h6>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    You can view your own profile and change your password
                  </ListGroup.Item>
                  
                  {user.is_admin && (
                    <>
                      <ListGroup.Item>
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        As an administrator, you have full access to the system
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        You can manage users, view audit logs, and perform all operations
                      </ListGroup.Item>
                    </>
                  )}
                  
                  {user.roles.includes('customer') && (
                    <ListGroup.Item>
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      As a customer, you can view vehicle instances you own
                    </ListGroup.Item>
                  )}
                  
                  {user.roles.includes('supplier') && (
                    <ListGroup.Item>
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      As a supplier, you can view vehicle instances for which you are the brand owner
                    </ListGroup.Item>
                  )}
                  
                  {user.roles.includes('technician') && (
                    <ListGroup.Item>
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      As a technician, you can view all vehicle instances
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default UserProfile;
